/**
 * Toast Notification Utility
 *
 * Enterprise-grade event-driven toast system.
 * Works with the ToastContainer component in App.jsx.
 *
 * Features:
 * - 4 types: success, error, warning, info
 * - Action buttons (e.g. "Undo") on any toast
 * - Promise toasts (loading → success/error)
 * - Deduplication (skip identical toasts within window)
 * - Queue management (max visible, FIFO overflow)
 * - Programmatic dismiss
 * - ARIA live-region compatible
 *
 * Usage:
 *   import toast from './utils/toast';
 *   toast.success('Saved!');
 *   toast.error('Failed', { duration: 8000 });
 *   toast.info('Update available', { action: { label: 'Reload', onClick: () => location.reload() } });
 *   toast.promise(fetchData(), { loading: 'Loading…', success: 'Done', error: 'Failed' });
 *   const id = toast.warning('Deleted', { action: { label: 'Undo', onClick: undoFn } });
 *   toast.dismiss(id);
 */

const TOAST_EVENT = 'show-toast';
const TOAST_DISMISS_EVENT = 'dismiss-toast';

// ─── Deduplication ───────────────────────────────────────────────────────────
const DEDUP_WINDOW_MS = 2000;
const _recentFingerprints = new Map(); // fingerprint → timestamp

const isDuplicate = (type, message) => {
  const fp = `${type}:${message}`;
  const last = _recentFingerprints.get(fp);
  if (last && Date.now() - last < DEDUP_WINDOW_MS) return true;
  _recentFingerprints.set(fp, Date.now());
  // Cleanup old entries periodically
  if (_recentFingerprints.size > 50) {
    const now = Date.now();
    for (const [key, ts] of _recentFingerprints) {
      if (now - ts > DEDUP_WINDOW_MS * 2) _recentFingerprints.delete(key);
    }
  }
  return false;
};

// ─── ID generator ────────────────────────────────────────────────────────────
let _toastCounter = 0;
const nextId = () => `toast_${++_toastCounter}`;

class Toast {
  /**
   * Show success toast
   * @param {string} message
   * @param {Object} [options] — { duration, action, deduplicate }
   * @returns {string} toast ID
   */
  success(message, options = {}) {
    return this._dispatch('success', message, { duration: 3000, ...options });
  }

  /**
   * Show error toast
   * @param {string} message
   * @param {Object} [options]
   * @returns {string} toast ID
   */
  error(message, options = {}) {
    return this._dispatch('error', message, { duration: 5000, ...options });
  }

  /**
   * Show warning toast
   * @param {string} message
   * @param {Object} [options]
   * @returns {string} toast ID
   */
  warning(message, options = {}) {
    return this._dispatch('warning', message, { duration: 5000, ...options });
  }

  /**
   * Show info toast
   * @param {string} message
   * @param {Object} [options]
   * @returns {string} toast ID
   */
  info(message, options = {}) {
    return this._dispatch('info', message, { duration: 5000, ...options });
  }

  /**
   * Show a loading toast that resolves to success/error based on promise outcome
   *
   * @param {Promise} promise — the async operation
   * @param {Object} messages — { loading, success, error }
   * @param {Object} [options] — shared options
   * @returns {Promise} — resolves/rejects with the original promise value
   *
   * Usage:
   *   toast.promise(saveProduct(data), {
   *     loading: 'Saving…',
   *     success: 'Product saved',
   *     error: (err) => `Failed: ${err.message}`,
   *   });
   */
  async promise(promise, messages = {}, options = {}) {
    const id = this._dispatch('loading', messages.loading || 'Loading…', {
      duration: 0, // don't auto-dismiss while loading
      ...options,
      id: nextId(),
    });

    try {
      const result = await promise;
      const successMsg = typeof messages.success === 'function'
        ? messages.success(result)
        : (messages.success || 'Done');
      this.dismiss(id);
      this.success(successMsg, options);
      return result;
    } catch (err) {
      const errorMsg = typeof messages.error === 'function'
        ? messages.error(err)
        : (messages.error || 'Something went wrong');
      this.dismiss(id);
      this.error(errorMsg, options);
      throw err;
    }
  }

  /**
   * Dismiss a specific toast by ID
   * @param {string} id
   */
  dismiss(id) {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(TOAST_DISMISS_EVENT, { detail: { id } }));
  }

  /**
   * Dismiss all toasts
   */
  dismissAll() {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(TOAST_DISMISS_EVENT, { detail: { all: true } }));
  }

  /**
   * Dispatch toast event
   * @private
   * @param {string} type
   * @param {string} message
   * @param {Object} options — { duration, action, deduplicate, id }
   *   action: { label: string, onClick: Function }
   * @returns {string} toast ID
   */
  _dispatch(type, message, options = {}) {
    if (typeof window === 'undefined') return '';

    const {
      duration = 3000,
      action = null,
      deduplicate = true,
      id: customId,
    } = options;

    // Deduplication — skip identical toasts within window
    if (deduplicate && type !== 'loading' && isDuplicate(type, message)) {
      return '';
    }

    const id = customId || nextId();

    const event = new CustomEvent(TOAST_EVENT, {
      detail: {
        id,
        type,
        message,
        duration,
        action, // { label, onClick }
        timestamp: Date.now(),
        // ARIA role for screen readers
        role: type === 'error' || type === 'warning' ? 'alert' : 'status',
        ariaLive: type === 'error' ? 'assertive' : 'polite',
      },
    });
    window.dispatchEvent(event);

    return id;
  }
}

export { TOAST_EVENT, TOAST_DISMISS_EVENT };
export default new Toast();
